import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useUser } from '@/hooks/use-user';
import {
  DealItem,
  DealItemWithVersions,
  ValidationError,
} from '@/lib/graphql/generated/graphql';
import { useTranslateValidationError } from '@/lib/hooks/useTranslateValidationError';
import { cn } from '@/lib/utils';
import { filterBrandVisibleValidationErrors } from '@/lib/utils/brand-validation-visibility';
import { useSetColumnSort } from '@/states/column-sort.state';
import { effectiveValidationErrorsAtom } from '@/states/effective-validation-errors.state';
import { ExtendedValidationError } from '@/types/validation-error';
import { Row } from '@tanstack/react-table';
import { useAtomValue } from 'jotai';
import { AlertCircle, AlertTriangle } from 'lucide-react';

import { ExtendedColumnDef } from '../table-products';
import { SortIcon } from './sort';

interface ValidationErrorsCellProps {
  rawValidationErrors: (ValidationError & {
    variantName?: string;
  })[];
  isParent: boolean;
}

/**
 * Visual config for the two severity groups shown in the validation popover.
 * Mirrors the AlertSidebar severity model so the two error types are clearly
 * delimited (colored header band + count badge). Unknown severities render
 * without a band (nothing is dropped).
 */
const SEVERITY_GROUP_CONFIG: Record<
  string,
  { labelKey: string; band: string; badge: string; icon: React.ReactNode }
> = {
  error: {
    labelKey: 'components.alert_sidebar.popover_to_fix',
    band: 'bg-red-50 text-red-500',
    badge: 'border-red-200 text-red-500',
    icon: <AlertCircle className="size-3.5" />,
  },
  warning: {
    labelKey: 'components.alert_sidebar.popover_to_check',
    band: 'bg-orange-50 text-orange-500',
    badge: 'border-orange-200 text-orange-500',
    icon: <AlertTriangle className="size-3.5" />,
  },
};

const ValidationErrorsCell: React.FC<ValidationErrorsCellProps> = ({
  rawValidationErrors,
  isParent,
}) => {
  const { t } = useTranslation();
  const translateValidationError = useTranslateValidationError();

  // Memoize translated errors - only recompute when raw errors or translation function change
  const translatedErrors = useMemo(() => {
    const severityRank: Record<string, number> = { error: 0, warning: 1 };
    return rawValidationErrors
      .map((error) => {
        const translated = translateValidationError(error);
        return {
          ...translated,
          variantName: error.variantName,
        };
      })
      .sort(
        (a, b) =>
          (severityRank[a.severity] ?? 2) - (severityRank[b.severity] ?? 2)
      );
  }, [rawValidationErrors, translateValidationError]);

  const severityGroups = useMemo(() => {
    const groups: { severity: string; items: typeof translatedErrors }[] = [];
    for (const error of translatedErrors) {
      const last = groups[groups.length - 1];
      if (last && last.severity === error.severity) {
        last.items.push(error);
      } else {
        groups.push({ severity: error.severity, items: [error] });
      }
    }
    return groups;
  }, [translatedErrors]);

  const getMainErrorIcon = (validationErrors: ExtendedValidationError[]) => {
    const hasError = validationErrors.some(
      (error) => error.severity === 'error'
    );
    if (hasError) {
      return <AlertCircle className="h-4 w-4 text-red-500" />;
    }
    return <AlertTriangle className="h-4 w-4 text-orange-500" />;
  };

  return (
    <div className="flex w-full flex-col items-center space-y-2 p-4 pl-3 text-left">
      {translatedErrors.length > 0 ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <div className={cn('cursor-help')}>
              {getMainErrorIcon(translatedErrors)}
            </div>
          </TooltipTrigger>
          <TooltipContent
            side="left"
            className="w-80 overflow-hidden rounded-lg border border-gray-200 bg-white p-0 shadow-lg"
          >
            {severityGroups.map((group) => {
              const cfg = SEVERITY_GROUP_CONFIG[group.severity];
              return (
                <div
                  key={group.severity}
                  className="border-b border-gray-100 last:border-0"
                >
                  {cfg && (
                    <div
                      className={cn(
                        'flex items-center gap-1.5 px-3 py-2 text-xs font-semibold',
                        cfg.band
                      )}
                    >
                      {cfg.icon}
                      <span>{t(cfg.labelKey)}</span>
                      <span
                        className={cn(
                          'ml-auto rounded-full border bg-white px-1.5 py-0.5 text-[11px] font-semibold',
                          cfg.badge
                        )}
                      >
                        {group.items.length}
                      </span>
                    </div>
                  )}
                  <div className="divide-y divide-gray-100">
                    {group.items.map((error, idx) => (
                      <div key={idx} className="px-3 py-2">
                        {isParent && error.variantName && (
                          <div className="mb-1 text-xs font-medium text-gray-500">
                            {t('general.variant')} : {error.variantName}
                          </div>
                        )}
                        <div className="text-sm font-medium text-gray-900">
                          {error.ruleName}
                        </div>
                        <div className="mt-0.5 text-sm text-gray-600">
                          {error.message}
                        </div>
                        {error.fixSuggestion && (
                          <div className="mt-1 text-xs italic text-blue-600">
                            {t('general.error_suggestion')}
                            {' : '}
                            {error.fixSuggestion}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </TooltipContent>
        </Tooltip>
      ) : (
        <div className="text-gray-300">—</div>
      )}
    </div>
  );
};

const ValidationErrorsColumnCell: React.FC<{
  row: Row<DealItemWithVersions>;
  isInternalUser: boolean;
}> = ({ row, isInternalUser }) => {
  const effectiveErrors = useAtomValue(effectiveValidationErrorsAtom);
  const isParent = row.subRows.length > 1;

  let rawValidationErrors: (ValidationError & { variantName?: string })[] = [];

  if (isParent) {
    if (row.subRows.length === 1) {
      rawValidationErrors = [];
    } else {
      const parentErrorsMap = new Map<
        ValidationError['ruleId'],
        ValidationError
      >();
      row.subRows.forEach((subRow) => {
        const errors = filterBrandVisibleValidationErrors(
          effectiveErrors.get(subRow.original.id) ?? [],
          isInternalUser
        );
        errors.forEach((error) => {
          if (!parentErrorsMap.has(error.ruleId)) {
            parentErrorsMap.set(error.ruleId, error);
          }
        });
      });
      rawValidationErrors = Array.from(parentErrorsMap.values());
    }
  } else {
    rawValidationErrors = filterBrandVisibleValidationErrors(
      effectiveErrors.get(row.original.id) ?? [],
      isInternalUser
    );
  }

  return (
    <ValidationErrorsCell
      rawValidationErrors={rawValidationErrors}
      isParent={isParent}
    />
  );
};

const useColumnDefValidationErrors =
  (): ExtendedColumnDef<DealItemWithVersions> => {
    const { setColumnSort } = useSetColumnSort();
    const isInternalUser = !!useUser()?.isInternalUser;

    const accessorKey: keyof DealItem = 'validationErrors';

    return useMemo(
      () => ({
        accessorKey: accessorKey,
        name: 'ValidationErrors',
        accessorFn: (row) => {
          const errors = row.activeVersion?.validationErrors;
          if (!errors) return 0;
          return filterBrandVisibleValidationErrors(errors, isInternalUser)
            .length;
        },
        sortingFn: 'alphanumeric',
        header: () => (
          <div className="flex items-center gap-1">
            <AlertTriangle className="h-4 w-4 text-orange-500" />
            <div
              className="flex flex-1 items-center gap-2"
              onClick={() => setColumnSort(accessorKey)}
            >
              <SortIcon pointer="validationErrors" />
            </div>
          </div>
        ),
        cell: ({ row }) => (
          <ValidationErrorsColumnCell
            row={row}
            isInternalUser={isInternalUser}
          />
        ),
        size: 60,
      }),
      [setColumnSort, isInternalUser]
    );
  };

export default useColumnDefValidationErrors;
